import { FC, UIEvent, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { EChartOption } from 'echarts'
import en from 'src/constants/en'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/arrow-down-s-line.svg'
import { ReactComponent as CheckboxActive } from 'src/assets/svg/checkbox-active.svg'
import { ReactComponent as CheckboxInactive } from 'src/assets/svg/checkbox-inactive.svg'
import { ReactComponent as CheckBox } from 'src/assets/svg/checkbox-white.svg'
import { ReactComponent as FilterChecked } from 'src/assets/svg/checked.svg'
import { ReactComponent as SentimentChange } from 'src/assets/svg/change-sentiment.svg'
import { ReactComponent as DeselectSVG } from 'src/assets/svg/deselect.svg'
import { ReactComponent as StatusDot } from 'src/assets/svg/status-dot-red.svg'
import { EChart } from 'src/charts/EChart'
import { COLORS } from 'src/constants/colors'
import useOutsideClick from 'src/hooks/useOutsideClick'
import {
  PLATFORM_LABEL,
  PLATFORM_ORDER,
} from 'src/pages/ReviewAnalysis/constants'
import { useChangeReviewSentiment } from 'src/reactQuery/useChangeReviewSentimentMutation'
import { useGetReviewAnalysisReviewsQuery } from 'src/reactQuery/useGetReviewAnalysisReviewsQuery'
import { useReviewAnalysisDateRange } from 'src/hooks/useReviewAnalysisDateRange'
import {
  ReviewBranch,
  ReviewListItem,
  ReviewPlatform,
  ReviewSentiment,
} from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import { ReviewCard } from './ReviewCard'
import styles from './reviewSentimentAnalysesSection.module.scss'

interface ReviewSentimentAnalysesSectionProps {
  campaignId: string
  // When provided, the widget is locked to this facility (facility page).
  facilityId?: string
  // several facilities (the platform page's multi-select)
  facilityIds?: string[]
  // When provided, the widget is locked to this platform (platform page).
  platform?: ReviewPlatform
  // When provided, the widget is locked to a single location within the facility.
  venueName?: string
}

type TabValue = 'ALL' | ReviewSentiment

const TABS: { label: string; value: TabValue }[] = [
  { label: en.all, value: 'ALL' },
  { label: en.positive, value: 'POS' },
  { label: en.negative, value: 'NEG' },
  { label: en.neutral, value: 'NEU' },
]

const RATINGS = [5, 4, 3, 2, 1]

// The right-panel filters. They are edited as a draft and applied on Save;
// the sentiment tabs above the list apply at once.
interface PanelFilters {
  // `platform::venueName` keys; empty = all branches
  branches: string[]
  platforms: ReviewPlatform[]
  ratings: number[]
  search: string
}

const EMPTY_FILTERS: PanelFilters = {
  branches: [],
  platforms: [],
  ratings: [],
  search: '',
}

const branchKey = (b: ReviewBranch) => `${b.platform}::${b.venueName}`
const parseBranchKey = (key: string): ReviewBranch => {
  const at = key.indexOf('::')
  return {
    platform: key.slice(0, at) as ReviewPlatform,
    venueName: key.slice(at + 2),
  }
}

const sameFilters = (a: PanelFilters, b: PanelFilters) =>
  JSON.stringify(a) === JSON.stringify(b)

const toggled = <T,>(list: T[], item: T) =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item]

const PAGE_SIZE = 20

export const ReviewSentimentAnalysesSection: FC<
  ReviewSentimentAnalysesSectionProps
> = ({
  campaignId,
  facilityId: scopeFacilityId,
  facilityIds,
  platform,
  venueName,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>('ALL')
  const [applied, setApplied] = useState<PanelFilters>(EMPTY_FILTERS)
  const [draft, setDraft] = useState<PanelFilters>(EMPTY_FILTERS)
  const isDirty = !sameFilters(draft, applied)
  const [branchOpen, setBranchOpen] = useState(false)
  const [branchSearch, setBranchSearch] = useState('')
  const [platformOpen, setPlatformOpen] = useState(false)
  // Multi-select for the bulk "change sentiment" action (mirrors social mentions).
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
  const [sentimentMenuOpen, setSentimentMenuOpen] = useState(false)
  const sentimentMenuRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    onOutsideClick: () => setSentimentMenuOpen(false),
    ref: sentimentMenuRef,
  })

  const effectiveFacilityId = scopeFacilityId

  const dateRange = useReviewAnalysisDateRange()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetReviewAnalysisReviewsQuery(campaignId, {
      branches: applied.branches.map(parseBranchKey),
      facilityId: effectiveFacilityId,
      facilityIds,
      from: dateRange?.from,
      limit: PAGE_SIZE,
      platform,
      platforms: platform ? undefined : applied.platforms,
      ratings: applied.ratings,
      search: applied.search.trim() || undefined,
      sentiment: activeTab === 'ALL' ? undefined : activeTab,
      to: dateRange?.to,
      venueName,
    })

  // Per-platform totals drive the Platform multi-select (hidden when the host
  // page already locks the widget to one platform).
  const platformCounts = data?.pages[0]?.platformCounts
  const availablePlatforms = useMemo(
    () => PLATFORM_ORDER.filter((p) => (platformCounts?.[p] ?? 0) > 0),
    [platformCounts]
  )
  const totalPlatformCount = useMemo(
    () =>
      availablePlatforms.reduce(
        (sum, p) => sum + (platformCounts?.[p] ?? 0),
        0
      ),
    [availablePlatforms, platformCounts]
  )
  const allPlatformsSelected = draft.platforms.length === 0

  const togglePlatform = (p: ReviewPlatform) =>
    setDraft((d) => ({ ...d, platforms: toggled(d.platforms, p) }))

  // Branches (a location on a platform) in scope; the list is offered only
  // when there is more than one and no single location is locked.
  const branchCounts = data?.pages[0]?.branchCounts ?? []
  const showBranches = !venueName && branchCounts.length > 1
  const visibleBranches = branchCounts.filter((b) =>
    b.venueName.toLowerCase().includes(branchSearch.trim().toLowerCase())
  )
  const allBranchesSelected = draft.branches.length === 0
  const toggleBranch = (key: string) =>
    setDraft((d) => ({ ...d, branches: toggled(d.branches, key) }))

  // A branch belongs to one platform and location, so a new scope (another
  // platform tab, location or facility) starts the selection over. Kept, the
  // old branches would narrow the new scope to nothing, with the dropdown no
  // longer showing them.
  useEffect(() => {
    const clearBranches = (f: PanelFilters) =>
      f.branches.length ? { ...f, branches: [] } : f
    setApplied(clearBranches)
    setDraft(clearBranches)
  }, [platform, venueName, scopeFacilityId])

  // Branches that drop out of scope (e.g. after a date change) are dropped
  // from the selection too. branchCounts ignores the selection, so a branch
  // missing from it has no reviews in scope.
  const loadedBranchCounts = data?.pages[0]?.branchCounts
  useEffect(() => {
    if (!loadedBranchCounts) return
    const inScope = new Set(loadedBranchCounts.map(branchKey))
    const keepInScope = (f: PanelFilters) =>
      f.branches.every((key) => inScope.has(key))
        ? f
        : { ...f, branches: f.branches.filter((key) => inScope.has(key)) }
    setApplied(keepInScope)
    setDraft(keepInScope)
  }, [loadedBranchCounts])

  // Sentiment totals are the same on every page; read them off the first.
  const counts = data?.pages[0]?.sentimentCounts
  const positives = counts?.positives ?? 0
  const negatives = counts?.negatives ?? 0
  const neutrals = counts?.neutrals ?? 0
  const total = positives + negatives + neutrals
  const positivePercentage = total ? (positives / total) * 100 : 0

  // Compact donut, mirroring the campaign page's sentiment chart (ring with the
  // resting % in the center; hovering a slice shows that slice's share).
  const chartOptions = useMemo<
    EChartOption<EChartOption.SeriesPie> | undefined
  >(() => {
    if (!total) return undefined
    return {
      animation: false,
      color: [COLORS.malibu, COLORS.burningOrange, COLORS.grey200],
      series: [
        {
          data: [
            { name: en.positive, value: positives },
            { name: en.negative, value: negatives },
            { name: en.neutral, value: neutrals },
          ],
          hoverAnimation: false,
          label: { show: false },
          labelLine: { show: false },
          radius: ['60%', '85%'],
          type: 'pie',
        },
      ],
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        confine: true,
        extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);',
        formatter: (params: { name: string; value: number }) =>
          `${params.name} : ${params.value}`,
        show: true,
        textStyle: { color: '#25262D', fontSize: 14 },
        trigger: 'item',
      },
    } as EChartOption<EChartOption.SeriesPie>
  }, [total, positives, negatives, neutrals])

  // Resting label is derived from the current counts (no lag); hover overrides
  // it with the hovered slice's share until the cursor leaves.
  const [hoverPercent, setHoverPercent] = useState<number | null>(null)
  const displayPercent = hoverPercent ?? positivePercentage

  const toggleRating = (rating: number) =>
    setDraft((d) => ({ ...d, ratings: toggled(d.ratings, rating) }))

  const saveFilters = () => {
    setApplied(draft)
    setBranchOpen(false)
  }
  const cancelFilters = () => {
    setDraft(applied)
    setBranchOpen(false)
  }
  // Reset clears and applies at once, tabs included.
  const resetFilters = () => {
    setActiveTab('ALL')
    setApplied(EMPTY_FILTERS)
    setDraft(EMPTY_FILTERS)
    setBranchSearch('')
  }

  const reviews = data?.pages.flatMap((p) => p.reviews) ?? []

  const { mutate: changeReviewSentiment } = useChangeReviewSentiment({
    campaignId,
    onSuccess: () => setCheckedIds(new Set()),
  })

  // Changing any filter (tab, rating, platform, facility, search, date range)
  // starts the selection over, as in the Inbox comments list: rows the user
  // can no longer see are rows they have lost track of, and "Mark as" must
  // never re-label reviews that were checked under a previous filter.
  useEffect(() => {
    setCheckedIds(new Set())
    setSentimentMenuOpen(false)
  }, [activeTab, applied, effectiveFacilityId, dateRange?.from, dateRange?.to])

  const toggleSelect = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = reviews.length > 0 && checkedIds.size === reviews.length
  const toggleSelectAll = () => {
    setCheckedIds(allSelected ? new Set() : new Set(reviews.map((r) => r._id)))
  }

  const applySentiment = (sentiment: ReviewSentiment) => {
    if (checkedIds.size === 0) return
    changeReviewSentiment({ ids: Array.from(checkedIds), sentiment })
    setSentimentMenuOpen(false)
  }

  const SENTIMENT_OPTIONS: { label: string; value: ReviewSentiment }[] = [
    { label: en.positive, value: 'POS' },
    { label: en.negative, value: 'NEG' },
    { label: en.neutral, value: 'NEU' },
  ]

  const renderReview = (review: ReviewListItem) => (
    <ReviewCard
      checked={checkedIds.has(review._id)}
      highlight={applied.search}
      key={review._id}
      onToggleSelect={toggleSelect}
      review={review}
    />
  )

  // Load the next page when the user scrolls near the bottom of the list.
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) return
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 200) {
      fetchNextPage()
    }
  }

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <StatusDot className={styles.statusDot} />
          <div className={styles.title}>{en.sentimentAnalyses}</div>
        </div>
      </div>

      <div className={styles.tabsRow}>
        {TABS.map((tab) => (
          <button
            className={classNames(
              styles.tab,
              activeTab === tab.value && styles.tabActive
            )}
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.chartWrapper}>
        <div className={styles.leftSection}>
          {checkedIds.size > 0 && (
            <div className={styles.selectionWrapper}>
              <div className={styles.icons}>
                <div
                  className={styles.iconWrapperCheck}
                  onClick={toggleSelectAll}
                >
                  {allSelected ? (
                    <FilterChecked className={styles.checkedIcon} />
                  ) : (
                    <CheckBox className={styles.checkedIcon} />
                  )}
                  <div className={styles.infoMessageSelect}>{en.selectAll}</div>
                </div>

                <div
                  className={styles.iconWrapperDeselect}
                  onClick={() => setCheckedIds(new Set())}
                >
                  <DeselectSVG className={styles.deselectIcon} />
                  <div className={styles.infoMessageDeselect}>
                    {en.deselectAll}
                  </div>
                </div>

                <div className={styles.line} />

                <div
                  className={styles.iconWrapperSentiment}
                  data-set={sentimentMenuOpen}
                  ref={sentimentMenuRef}
                >
                  <SentimentChange
                    onClick={() => setSentimentMenuOpen((v) => !v)}
                  />
                  <div className={styles.infoMessageSentiment}>
                    {en.changeSentiment}
                  </div>
                  <div
                    className={styles.changeSentimentModal}
                    data-set={sentimentMenuOpen}
                  >
                    {SENTIMENT_OPTIONS.map((option) => (
                      <button
                        className={styles.changeSentimentModalItem}
                        key={option.value}
                        onClick={() => applySentiment(option.value)}
                        type="button"
                      >
                        <div className={styles.sentimentItem}>
                          <div
                            className={styles.sentimentItemColor}
                            data-set={option.label}
                          />
                          <div>
                            Mark as: <b>{option.label}</b>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <span className={styles.selectedCount}>
                {en.selectedReviews}{' '}
                <span className={styles.selectedCountNumber}>
                  ({checkedIds.size})
                </span>
              </span>
            </div>
          )}
          <div className={styles.reviewList} onScroll={handleScroll}>
            {reviews.length ? (
              reviews.map(renderReview)
            ) : (
              <div className={styles.emptyState}>
                {isLoading ? en.loading : en.noPostsAvailable}
              </div>
            )}
            {isFetchingNextPage && (
              <div className={styles.loadingMore}>{en.loading}</div>
            )}
          </div>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.chartSection}>
            <div className={styles.chartContainer}>
              {chartOptions && (
                <EChart
                  onMouseOut={() => setHoverPercent(null)}
                  onMouseOver={(event) => setHoverPercent(event.percent ?? 0)}
                  options={chartOptions}
                />
              )}
              <div className={styles.chartLabel}>
                {displayPercent.toFixed(0)}%
              </div>
            </div>
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <span
                  className={classNames(styles.legendDot, styles.positive)}
                />
                <span>{en.positive}</span>
              </div>
              <div className={styles.legendItem}>
                <span
                  className={classNames(styles.legendDot, styles.negative)}
                />
                <span>{en.negative}</span>
              </div>
              <div className={styles.legendItem}>
                <span
                  className={classNames(styles.legendDot, styles.neutral)}
                />
                <span>{en.neutral}</span>
              </div>
            </div>
          </div>

          <div className={styles.filterCard}>
            <input
              className={styles.searchInput}
              onChange={(e) =>
                setDraft((d) => ({ ...d, search: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveFilters()
              }}
              placeholder={en.searchByTextOrUrl}
              type="text"
              value={draft.search}
            />

            {!platform && availablePlatforms.length > 0 && (
              <div className={styles.platformFilter}>
                <button
                  className={styles.platformHeader}
                  onClick={() => setPlatformOpen((v) => !v)}
                  type="button"
                >
                  <span className={styles.platformHeaderLabel}>
                    {en.platform}
                  </span>
                  <span className={styles.platformHeaderValue}>
                    <span className={styles.platformHeaderHighlight}>
                      {allPlatformsSelected ? en.all : draft.platforms.length}
                    </span>{' '}
                    {en.platformSelected}
                    <ChevronIcon
                      className={classNames(
                        styles.platformChevron,
                        platformOpen && styles.platformChevronUp
                      )}
                    />
                  </span>
                </button>

                {platformOpen && (
                  <div className={styles.platformDropdown}>
                    <button
                      className={classNames(
                        styles.platformOption,
                        allPlatformsSelected && styles.platformOptionActive
                      )}
                      onClick={() => setDraft((d) => ({ ...d, platforms: [] }))}
                      type="button"
                    >
                      {allPlatformsSelected ? (
                        <CheckboxActive className={styles.platformCheckbox} />
                      ) : (
                        <CheckboxInactive className={styles.platformCheckbox} />
                      )}
                      <span className={styles.platformName}>
                        {en.allSources}
                      </span>
                      <span className={styles.platformDot}>•</span>
                      <span className={styles.platformCount}>
                        {totalPlatformCount.toLocaleString()}
                      </span>
                    </button>

                    {availablePlatforms.map((p) => {
                      const selected = draft.platforms.includes(p)
                      return (
                        <button
                          className={classNames(
                            styles.platformOption,
                            selected && styles.platformOptionActive
                          )}
                          key={p}
                          onClick={() => togglePlatform(p)}
                          type="button"
                        >
                          {selected ? (
                            <CheckboxActive
                              className={styles.platformCheckbox}
                            />
                          ) : (
                            <CheckboxInactive
                              className={styles.platformCheckbox}
                            />
                          )}
                          <span className={styles.platformName}>
                            {PLATFORM_LABEL[p]}
                          </span>
                          <span className={styles.platformDot}>•</span>
                          <span className={styles.platformCount}>
                            {(platformCounts?.[p] ?? 0).toLocaleString()}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {showBranches && (
              <div className={styles.branchFilter}>
                <div className={styles.branchHeader}>
                  <input
                    className={styles.branchSearch}
                    onChange={(e) => {
                      setBranchSearch(e.target.value)
                      setBranchOpen(true)
                    }}
                    onFocus={() => setBranchOpen(true)}
                    placeholder="Branches"
                    type="text"
                    value={branchSearch}
                  />
                  <button
                    className={styles.branchToggle}
                    onClick={() => setBranchOpen((v) => !v)}
                    type="button"
                  >
                    <span className={styles.platformHeaderHighlight}>
                      {allBranchesSelected ? en.all : draft.branches.length}
                    </span>{' '}
                    {draft.branches.length === 1 ? 'Branch' : 'Branches'}{' '}
                    Selected
                    <ChevronIcon
                      className={classNames(
                        styles.platformChevron,
                        branchOpen && styles.platformChevronUp
                      )}
                    />
                  </button>
                </div>

                {branchOpen && (
                  <div className={styles.branchList}>
                    {!branchSearch.trim() && (
                      <button
                        className={classNames(
                          styles.platformOption,
                          allBranchesSelected && styles.platformOptionActive
                        )}
                        onClick={() =>
                          setDraft((d) => ({ ...d, branches: [] }))
                        }
                        type="button"
                      >
                        {allBranchesSelected ? (
                          <CheckboxActive className={styles.platformCheckbox} />
                        ) : (
                          <CheckboxInactive
                            className={styles.platformCheckbox}
                          />
                        )}
                        <span className={styles.platformName}>
                          All Branches
                        </span>
                      </button>
                    )}
                    {visibleBranches.map((branch) => {
                      const key = branchKey(branch)
                      const selected = draft.branches.includes(key)
                      return (
                        <button
                          className={classNames(
                            styles.platformOption,
                            selected && styles.platformOptionActive
                          )}
                          key={key}
                          onClick={() => toggleBranch(key)}
                          title={branch.venueName}
                          type="button"
                        >
                          {selected ? (
                            <CheckboxActive
                              className={styles.platformCheckbox}
                            />
                          ) : (
                            <CheckboxInactive
                              className={styles.platformCheckbox}
                            />
                          )}
                          <span className={styles.branchName}>
                            {branch.venueName}
                          </span>
                          {/* the platform is only worth naming when the
                              widget is not already locked to one */}
                          {!platform && (
                            <>
                              <span className={styles.platformDot}>•</span>
                              <span className={styles.branchPlatform}>
                                {PLATFORM_LABEL[branch.platform]}
                              </span>
                            </>
                          )}
                        </button>
                      )
                    })}
                    {visibleBranches.length === 0 && (
                      <span className={styles.branchEmpty}>
                        No branch matches
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>{en.evaluation}</span>
              <div className={styles.ratingsRow}>
                {RATINGS.map((rating) => (
                  <button
                    className={classNames(
                      styles.ratingChip,
                      draft.ratings.includes(rating) && styles.ratingChipActive
                    )}
                    key={rating}
                    onClick={() => toggleRating(rating)}
                    type="button"
                  >
                    {rating}★
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterActions}>
              <button
                className={styles.resetBtn}
                onClick={resetFilters}
                type="button"
              >
                {en.resetFilter}
              </button>
              <div className={styles.filterActionsRight}>
                <button
                  className={styles.cancelBtn}
                  disabled={!isDirty}
                  onClick={cancelFilters}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={styles.saveBtn}
                  disabled={!isDirty}
                  onClick={saveFilters}
                  type="button"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
